/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { TextField } from '@mui/material';
import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useFormFieldsTranslation } from '../utils/i18n';

export function decimalToDegreesMinutes(value: number): string {
  const sign = value < 0 ? '-' : '';
  let abs = Math.abs(value);

  const degrees = Math.floor(abs);
  const minutes = Math.round((abs - degrees) * 60);

  // normalize rounding overflow (e.g. 59.9 min → +1 degree)
  if (minutes === 60) {
    abs += 1;
    return decimalToDegreesMinutes(sign === '-' ? -abs : abs);
  }

  return `${sign}${degrees}°${minutes.toString().padStart(2, '0')}′`;
}

/**
 * Parse a cardinal direction prefix (N/S/E/W) from input string.
 * Returns { negative: boolean, remaining: string }
 */
function parseCardinalDirection(
  input: string,
  isLongitude: boolean,
): { negative: boolean; remaining: string } {
  const upperInput = input.toUpperCase().trim();
  const firstChar = upperInput.charAt(0);
  const remaining = upperInput.slice(1);

  // Check for valid cardinal direction based on coordinate type
  const validDirections = isLongitude ? ['W', 'E'] : ['S', 'N'];
  if (!validDirections.includes(firstChar)) {
    return { negative: false, remaining: input };
  }

  switch (firstChar) {
    case 'W':
    case 'S':
      return { negative: true, remaining };
    case 'E':
    case 'N':
      return { negative: false, remaining };
    default:
      return { negative: false, remaining: input };
  }
}

/**
 * Check if the raw input has valid minutes (0-59).
 * For latitude: digits > 2 means DDMM format
 * For longitude: digits > 3 means DDDMM format
 * Returns true if valid, false otherwise.
 */
export function hasValidMinutes(
  input: string | number,
  isLongitude = false,
): boolean {
  const raw = input.toString().trim();
  const digits = raw.replace(/[^0-9]/g, '');

  // For latitude: <= 2 digits is degrees only
  // For longitude: <= 3 digits is degrees only
  const degreesOnlyMaxLen = isLongitude ? 3 : 2;

  if (digits.length <= degreesOnlyMaxLen) {
    return true; // Only degrees, no minutes to validate
  }

  const min = parseInt(digits.slice(-2), 10);
  return min >= 0 && min <= 59;
}

export function degreesMinutesToDecimal(
  input: string | number,
  isLongitude = false,
): number {
  const raw = input.toString().trim();

  // Parse cardinal direction prefix (W/E/S/N)
  const { negative: cardinalNegative, remaining } = parseCardinalDirection(
    raw,
    isLongitude,
  );

  // Check for minus sign in remaining input
  const hasMinusSign = remaining.startsWith('-');
  const digits = remaining.replace(/[^0-9]/g, '');

  // Determine sign: either from cardinal direction or minus sign
  const sign = cardinalNegative || hasMinusSign ? -1 : 1;

  // For latitude: <= 2 digits is degrees only (e.g., "90" = 90°)
  // For longitude: <= 3 digits is degrees only (e.g., "180" = 180°)
  const degreesOnlyMaxLen = isLongitude ? 3 : 2;

  if (digits.length <= degreesOnlyMaxLen) {
    const deg = parseInt(digits || '0', 10);
    return sign * deg;
  }

  // split into degrees + minutes
  const deg = parseInt(digits.slice(0, digits.length - 2), 10);
  const min = parseInt(digits.slice(-2), 10);

  // NOTE: We no longer normalize minutes overflow here.
  // Invalid minutes (>59) will be rejected by validation.
  // This preserves the raw value for validation purposes.
  return sign * (deg + min / 60);
}

interface Props {
  name: string;
  label: string;
  disabled?: boolean;
  isReadOnly?: boolean;
  size?: 'small' | 'medium';
  isLongitude?: boolean;
}

export const DegreesMinutesField: React.FC<Props> = ({
  name,
  label,
  disabled = false,
  isReadOnly = false,
  size = 'small',
  isLongitude = false,
}) => {
  const { control } = useFormContext();
  const { t } = useFormFieldsTranslation();
  const [localInput, setLocalInput] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  // Track raw input when there's a validation error so we show what user typed
  const [rawInputOnError, setRawInputOnError] = useState<string | null>(null);

  // Error key constants to distinguish error types
  const INVALID_MINUTES_ERROR = 'invalidMinutes';
  const INVALID_FORMAT_ERROR = 'invalidFormat';

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const { value, onChange } = field;

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
          const rawValue = e.target.value;

          // Filter to only allow valid characters:
          // - Digits (0-9)
          // - Minus sign (-) only at the beginning
          // - Cardinal directions (W/E/N/S for longitude, N/S for latitude) only at the beginning
          // - Common formatting chars (°, ′, ', *) which will be stripped on blur
          const validCardinals = isLongitude ? /^[WwEe]/ : /^[NnSs]/;
          let filtered = '';

          for (let i = 0; i < rawValue.length; i += 1) {
            const char = rawValue[i];
            const upperChar = char.toUpperCase();

            if (/[0-9]/.test(char)) {
              // Always allow digits
              filtered += char;
            } else if (char === '-' && filtered === '') {
              // Allow minus sign only at the beginning
              filtered += char;
            } else if (
              i === 0 &&
              validCardinals.test(upperChar) &&
              filtered === ''
            ) {
              // Allow valid cardinal direction only at the very beginning
              filtered += upperChar;
            } else if (/[°′'*]/.test(char)) {
              // Allow common formatting characters (will be stripped on blur)
              filtered += char;
            }
            // All other characters are silently ignored
          }

          setLocalInput(filtered);
          setIsEditing(true);
          // Clear error state when user starts typing
          setRawInputOnError(null);
        };

        const handleBlur = (): void => {
          // Handle clearing the field
          if (localInput === '' || localInput === null) {
            onChange(null);
            setIsEditing(false);
            setLocalInput(null);
            setRawInputOnError(null);
            return;
          }

          // Handle just a minus sign
          if (localInput === '-') {
            setIsEditing(false);
            setLocalInput(null);
            return;
          }

          // Parse cardinal direction prefix (W/E/S/N)
          const {
            negative: isNegativeFromCardinal,
            remaining: remainingInput,
          } = parseCardinalDirection(localInput, isLongitude);

          const input = remainingInput.replace(/[^\d-]/g, '');
          const hasMinusSign = input.startsWith('-');
          const digits = input.replace(/[^0-9]/g, '');

          // Latitude: max 4 digits (DDMM), Longitude: max 5 (DDDMM)
          const maxLen = isLongitude ? 5 : 4;
          if (digits.length > maxLen) {
            // Too many digits - show error but keep user's input visible
            setRawInputOnError(localInput);
            onChange(INVALID_FORMAT_ERROR);
            setIsEditing(false);
            setLocalInput(null);
            return;
          }

          // Check if minutes are valid (0-59) based on coordinate type
          // For latitude: > 2 digits means DDMM format
          // For longitude: > 3 digits means DDDMM format
          const degreesOnlyMaxLen = isLongitude ? 3 : 2;
          if (digits.length > degreesOnlyMaxLen) {
            const minutes = parseInt(digits.slice(-2), 10);
            if (minutes > 59) {
              // Invalid minutes - show error but keep user's input visible
              setRawInputOnError(localInput);
              onChange(INVALID_MINUTES_ERROR);
              setIsEditing(false);
              setLocalInput(null);
              return;
            }
          }

          // Determine if result should be negative
          const isNegative = isNegativeFromCardinal || hasMinusSign;
          const normalized = isNegative ? `-${digits}` : digits;

          try {
            const decimal = degreesMinutesToDecimal(normalized, isLongitude);
            onChange(decimal);
            setRawInputOnError(null);
          } catch {
            // Invalid, do not update form value
          }

          setIsEditing(false);
          setLocalInput(null);
        };

        let displayValue: string;
        if (isEditing) {
          displayValue = localInput ?? '';
        } else if (fieldState.error && rawInputOnError !== null) {
          // Show the invalid input the user typed when there's an error
          displayValue = rawInputOnError;
        } else if (value != null && typeof value === 'number') {
          displayValue = decimalToDegreesMinutes(value);
        } else {
          displayValue = '';
        }

        return (
          <TextField
            disabled={disabled}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            label={label}
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={isLongitude ? 'DDD°MM′' : 'DD°MM′'}
            size={size}
            slotProps={{
              input: {
                readOnly: isReadOnly,
              },
            }}
            value={displayValue}
          />
        );
      }}
      rules={{
        validate: (value: number | string) => {
          if (value == null) {
            return true;
          } // let empty pass

          // Check for special error markers
          if (value === INVALID_MINUTES_ERROR) {
            return t('form-fields-error-invalid-minutes');
          }
          if (value === INVALID_FORMAT_ERROR) {
            return t('form-fields-error-invalid-coordinate-format');
          }

          // Must be a number for range checks
          if (typeof value !== 'number' || isNaN(value)) {
            return true;
          }

          if (!isLongitude && (value < -90 || value > 90)) {
            return t('form-fields-error-latitude-minutes');
          }
          if (isLongitude && (value < -180 || value > 180)) {
            return t('form-fields-error-longitude-minutes');
          }
          return true;
        },
      }}
    />
  );
};
